import React, { createContext, useContext, useReducer, useCallback } from 'react';
import apiService, { handleApiError } from '../services/api';

// Initial state
const initialState = {
  categories: [],
  destinations: [],
  tours: [],
  activities: [],
  resorts: [],
  tourGuides: [],
  services: [],
  shopItems: [],
  blogPosts: [],
  galleryImages: [],
  pricingPlans: [],
  faqs: [],
  aboutSection: null,
  heroSlides: [],
  // Airbnb content
  airbnb: {
    heroSlides: [],
    aboutSection: null,
    propertySection: null,
    exploreSection: null,
    teamSection: null,
    contactSection: null,
    testimonials: [],
    blogSection: null,
    blogPosts: [],
    accommodations: [],
    accommodationTypes: [],
    amenities: [],
  },
  // AirTicket content
  airTicket: {
    heroSlides: [],
    destinations: [],
    categories: [],
    aboutSection: null,
    tours: [],
    gallery: [],
    agents: [],
    services: [],
    contact: null,
    testimonials: [],
    brands: [],
    blogPosts: [],
  },
  featuredContent: {
    destinations: [],
    tours: [],
    activities: [],
    resorts: [],
    services: [],
    shopItems: [],
    blogPosts: [],
    galleryImages: [],
    // Airbnb featured content
    airbnbTestimonials: [],
    airbnbBlogPosts: [],
    accommodations: [],
  },
  loading: {
    categories: false,
    destinations: false,
    tours: false,
    activities: false,
    resorts: false,
    tourGuides: false,
    services: false,
    shopItems: false,
    blogPosts: false,
    galleryImages: false,
    pricingPlans: false,
    faqs: false,
    aboutSection: false,
    heroSlides: false,
    // Airbnb loading states
    airbnbHeroSlides: false,
    airbnbAboutSection: false,
    airbnbPropertySection: false,
    airbnbExploreSection: false,
    airbnbTeamSection: false,
    airbnbContactSection: false,
    airbnbTestimonials: false,
    airbnbBlogSection: false,
    airbnbBlogPosts: false,
    accommodations: false,
    accommodationTypes: false,
    amenities: false,
    // AirTicket loading states
    airTicketHeroSlides: false,
    airTicketDestinations: false,
    airTicketCategories: false,
    airTicketAboutSection: false,
    airTicketTours: false,
    airTicketGallery: false,
    airTicketAgents: false,
    airTicketServices: false,
    airTicketContact: false,
    airTicketTestimonials: false,
    airTicketBrands: false,
    airTicketBlogPosts: false,
  },
  errors: {},
  searchResults: null,
  dashboardStats: null,
};

// Action types
const actionTypes = {
  SET_LOADING: 'SET_LOADING',
  SET_ERROR: 'SET_ERROR',
  SET_CATEGORIES: 'SET_CATEGORIES',
  SET_DESTINATIONS: 'SET_DESTINATIONS',
  SET_TOURS: 'SET_TOURS',
  SET_ACTIVITIES: 'SET_ACTIVITIES',
  SET_RESORTS: 'SET_RESORTS',
  SET_TOUR_GUIDES: 'SET_TOUR_GUIDES',
  SET_SERVICES: 'SET_SERVICES',
  SET_SHOP_ITEMS: 'SET_SHOP_ITEMS',
  SET_BLOG_POSTS: 'SET_BLOG_POSTS',
  SET_GALLERY_IMAGES: 'SET_GALLERY_IMAGES',
  SET_PRICING_PLANS: 'SET_PRICING_PLANS',
  SET_FAQS: 'SET_FAQS',
  SET_ABOUT_SECTION: 'SET_ABOUT_SECTION',
  SET_HERO_SLIDES: 'SET_HERO_SLIDES',
  SET_FEATURED_CONTENT: 'SET_FEATURED_CONTENT',
  SET_SEARCH_RESULTS: 'SET_SEARCH_RESULTS',
  SET_DASHBOARD_STATS: 'SET_DASHBOARD_STATS',
  CLEAR_ERROR: 'CLEAR_ERROR',
  // Airbnb action types
  SET_AIRBNB_CONTENT: 'SET_AIRBNB_CONTENT',
  // AirTicket action types
  SET_AIRTICKET_CONTENT: 'SET_AIRTICKET_CONTENT',
};

// Reducer
const tourismReducer = (state, action) => {
  switch (action.type) {
    case actionTypes.SET_LOADING:
      return {
        ...state,
        loading: {
          ...state.loading,
          [action.payload.key]: action.payload.value,
        },
      };

    case actionTypes.SET_ERROR:
      return {
        ...state,
        errors: {
          ...state.errors,
          [action.payload.key]: action.payload.value,
        },
        loading: {
          ...state.loading,
          [action.payload.key]: false,
        },
      };

    case actionTypes.CLEAR_ERROR:
      const newErrors = { ...state.errors };
      delete newErrors[action.payload];
      return {
        ...state,
        errors: newErrors,
      };

    case actionTypes.SET_CATEGORIES:
      return {
        ...state,
        categories: action.payload,
        loading: { ...state.loading, categories: false },
      };

    case actionTypes.SET_DESTINATIONS:
      return {
        ...state,
        destinations: action.payload,
        loading: { ...state.loading, destinations: false },
      };

    case actionTypes.SET_TOURS:
      return {
        ...state,
        tours: action.payload,
        loading: { ...state.loading, tours: false },
      };

    case actionTypes.SET_ACTIVITIES:
      return {
        ...state,
        activities: action.payload,
        loading: { ...state.loading, activities: false },
      };

    case actionTypes.SET_RESORTS:
      return {
        ...state,
        resorts: action.payload,
        loading: { ...state.loading, resorts: false },
      };

    case actionTypes.SET_TOUR_GUIDES:
      return {
        ...state,
        tourGuides: action.payload,
        loading: { ...state.loading, tourGuides: false },
      };

    case actionTypes.SET_SERVICES:
      return {
        ...state,
        services: action.payload,
        loading: { ...state.loading, services: false },
      };

    case actionTypes.SET_SHOP_ITEMS:
      return {
        ...state,
        shopItems: action.payload,
        loading: { ...state.loading, shopItems: false },
      };

    case actionTypes.SET_BLOG_POSTS:
      return {
        ...state,
        blogPosts: action.payload,
        loading: { ...state.loading, blogPosts: false },
      };

    case actionTypes.SET_GALLERY_IMAGES:
      return {
        ...state,
        galleryImages: action.payload,
        loading: { ...state.loading, galleryImages: false },
      };

    case actionTypes.SET_PRICING_PLANS:
      return {
        ...state,
        pricingPlans: action.payload,
        loading: { ...state.loading, pricingPlans: false },
      };

    case actionTypes.SET_FAQS:
      return {
        ...state,
        faqs: action.payload,
        loading: { ...state.loading, faqs: false },
      };

    case actionTypes.SET_ABOUT_SECTION:
      return {
        ...state,
        aboutSection: action.payload,
        loading: { ...state.loading, aboutSection: false },
      };

    case actionTypes.SET_HERO_SLIDES:
      return {
        ...state,
        heroSlides: action.payload,
        loading: { ...state.loading, heroSlides: false },
      };

    case actionTypes.SET_FEATURED_CONTENT:
      return {
        ...state,
        featuredContent: {
          ...state.featuredContent,
          [action.payload.key]: action.payload.value,
        },
      };

    case actionTypes.SET_SEARCH_RESULTS:
      return {
        ...state,
        searchResults: action.payload,
      };

    case actionTypes.SET_DASHBOARD_STATS:
      return {
        ...state,
        dashboardStats: action.payload,
      };

    case actionTypes.SET_AIRBNB_CONTENT:
      return {
        ...state,
        airbnb: {
          ...state.airbnb,
          [action.payload.key]: action.payload.value,
        },
        loading: {
          ...state.loading,
          [action.payload.loadingKey || action.payload.key]: false,
        },
      };

    case actionTypes.SET_AIRTICKET_CONTENT:
      return {
        ...state,
        airTicket: {
          ...state.airTicket,
          [action.payload.key]: action.payload.value,
        },
        loading: {
          ...state.loading,
          [action.payload.loadingKey || action.payload.key]: false,
        },
      };

    default:
      return state;
  }
};

// Create contexts
const TourismContext = createContext();
const TourismDispatchContext = createContext();

// Provider component
export const TourismProvider = ({ children }) => {
  const [state, dispatch] = useReducer(tourismReducer, initialState);

  // Helper function to handle API calls
  const handleApiCall = useCallback(async (apiCall, actionType, key) => {
    dispatch({ type: actionTypes.SET_LOADING, payload: { key, value: true } });
    dispatch({ type: actionTypes.CLEAR_ERROR, payload: key });

    try {
      const data = await apiCall();
      
      // Handle both plain action types and function action creators
      if (typeof actionType === 'function') {
        const action = actionType(data);
        dispatch(action);
      } else {
        dispatch({ type: actionType, payload: data });
      }
      
      return data;
    } catch (error) {
      const errorMessage = handleApiError(error);
      dispatch({ 
        type: actionTypes.SET_ERROR, 
        payload: { key, value: errorMessage } 
      });
      throw error;
    }
  }, []);

  // Action creators
  const actions = {
    // Categories
    fetchCategories: useCallback(() => {
      return handleApiCall(
        () => apiService.getCategories(),
        actionTypes.SET_CATEGORIES,
        'categories'
      );
    }, [handleApiCall]),

    // Destinations
    fetchDestinations: useCallback((params) => {
      return handleApiCall(
        () => apiService.getDestinations(params),
        actionTypes.SET_DESTINATIONS,
        'destinations'
      );
    }, [handleApiCall]),

    fetchFeaturedDestinations: useCallback(() => {
      return handleApiCall(
        () => apiService.getFeaturedDestinations(),
        (data) => ({
          type: actionTypes.SET_FEATURED_CONTENT,
          payload: { key: 'destinations', value: data },
        }),
        'destinations'
      );
    }, [handleApiCall]),

    // Tours
    fetchTours: useCallback((params) => {
      return handleApiCall(
        () => apiService.getTours(params),
        actionTypes.SET_TOURS,
        'tours'
      );
    }, [handleApiCall]),

    fetchFeaturedTours: useCallback(() => {
      return handleApiCall(
        () => apiService.getFeaturedTours(),
        (data) => ({
          type: actionTypes.SET_FEATURED_CONTENT,
          payload: { key: 'tours', value: data },
        }),
        'tours'
      );
    }, [handleApiCall]),

    // Activities
    fetchActivities: useCallback((params) => {
      return handleApiCall(
        () => apiService.getActivities(params),
        actionTypes.SET_ACTIVITIES,
        'activities'
      );
    }, [handleApiCall]),

    fetchFeaturedActivities: useCallback(() => {
      return handleApiCall(
        () => apiService.getFeaturedActivities(),
        (data) => ({
          type: actionTypes.SET_FEATURED_CONTENT,
          payload: { key: 'activities', value: data },
        }),
        'activities'
      );
    }, [handleApiCall]),

    // Resorts
    fetchResorts: useCallback((params) => {
      return handleApiCall(
        () => apiService.getResorts(params),
        actionTypes.SET_RESORTS,
        'resorts'
      );
    }, [handleApiCall]),

    fetchFeaturedResorts: useCallback(() => {
      return handleApiCall(
        () => apiService.getFeaturedResorts(),
        (data) => ({
          type: actionTypes.SET_FEATURED_CONTENT,
          payload: { key: 'resorts', value: data },
        }),
        'resorts'
      );
    }, [handleApiCall]),

    // Tour Guides
    fetchTourGuides: useCallback((params) => {
      return handleApiCall(
        () => apiService.getTourGuides(params),
        actionTypes.SET_TOUR_GUIDES,
        'tourGuides'
      );
    }, [handleApiCall]),

    // Services
    fetchServices: useCallback((params) => {
      return handleApiCall(
        () => apiService.getServices(params),
        actionTypes.SET_SERVICES,
        'services'
      );
    }, [handleApiCall]),

    fetchFeaturedServices: useCallback(() => {
      return handleApiCall(
        () => apiService.getFeaturedServices(),
        (data) => ({
          type: actionTypes.SET_FEATURED_CONTENT,
          payload: { key: 'services', value: data },
        }),
        'services'
      );
    }, [handleApiCall]),

    // Shop
    fetchShopItems: useCallback((params) => {
      return handleApiCall(
        () => apiService.getShopItems(params),
        actionTypes.SET_SHOP_ITEMS,
        'shopItems'
      );
    }, [handleApiCall]),

    fetchFeaturedShopItems: useCallback(() => {
      return handleApiCall(
        () => apiService.getFeaturedShopItems(),
        (data) => ({
          type: actionTypes.SET_FEATURED_CONTENT,
          payload: { key: 'shopItems', value: data },
        }),
        'shopItems'
      );
    }, [handleApiCall]),

    // Blog
    fetchBlogPosts: useCallback((params) => {
      return handleApiCall(
        () => apiService.getBlogPosts(params),
        actionTypes.SET_BLOG_POSTS,
        'blogPosts'
      );
    }, [handleApiCall]),

    fetchFeaturedBlogPosts: useCallback(() => {
      return handleApiCall(
        () => apiService.getFeaturedBlogPosts(),
        (data) => ({
          type: actionTypes.SET_FEATURED_CONTENT,
          payload: { key: 'blogPosts', value: data },
        }),
        'blogPosts'
      );
    }, [handleApiCall]),

    // Gallery
    fetchGalleryImages: useCallback((params) => {
      return handleApiCall(
        () => apiService.getGalleryImages(params),
        actionTypes.SET_GALLERY_IMAGES,
        'galleryImages'
      );
    }, [handleApiCall]),

    fetchFeaturedGalleryImages: useCallback(() => {
      return handleApiCall(
        () => apiService.getFeaturedGalleryImages(),
        (data) => ({
          type: actionTypes.SET_FEATURED_CONTENT,
          payload: { key: 'galleryImages', value: data },
        }),
        'galleryImages'
      );
    }, [handleApiCall]),

    // Pricing
    fetchPricingPlans: useCallback((params) => {
      return handleApiCall(
        () => apiService.getPricingPlans(params),
        actionTypes.SET_PRICING_PLANS,
        'pricingPlans'
      );
    }, [handleApiCall]),

    // FAQs
    fetchFAQs: useCallback((params) => {
      return handleApiCall(
        () => apiService.getFAQs(params),
        actionTypes.SET_FAQS,
        'faqs'
      );
    }, [handleApiCall]),

    // About Section
    fetchAboutSection: useCallback(() => {
      return handleApiCall(
        () => apiService.getAboutSection(),
        actionTypes.SET_ABOUT_SECTION,
        'aboutSection'
      );
    }, [handleApiCall]),

    // Hero Slides
    fetchHeroSlides: useCallback(() => {
      return handleApiCall(
        () => apiService.getHeroSlides(),
        actionTypes.SET_HERO_SLIDES,
        'heroSlides'
      );
    }, [handleApiCall]),

    // Search
    searchAll: useCallback(async (query) => {
      try {
        const results = await apiService.searchAll(query);
        dispatch({ type: actionTypes.SET_SEARCH_RESULTS, payload: results });
        return results;
      } catch (error) {
        console.error('Search error:', error);
        throw error;
      }
    }, []),

    // Dashboard Stats
    fetchDashboardStats: useCallback(async () => {
      try {
        const stats = await apiService.getDashboardStats();
        dispatch({ type: actionTypes.SET_DASHBOARD_STATS, payload: stats });
        return stats;
      } catch (error) {
        console.error('Dashboard stats error:', error);
        throw error;
      }
    }, []),

    // Contact
    submitContact: useCallback(async (contactData) => {
      try {
        return await apiService.submitContact(contactData);
      } catch (error) {
        console.error('Contact submission error:', error);
        throw error;
      }
    }, []),

    // Reviews
    submitReview: useCallback(async (reviewData) => {
      try {
        return await apiService.submitReview(reviewData);
      } catch (error) {
        console.error('Review submission error:', error);
        throw error;
      }
    }, []),

    // Clear specific error
    clearError: useCallback((key) => {
      dispatch({ type: actionTypes.CLEAR_ERROR, payload: key });
    }, []),

    // Airbnb Hero Slides
    fetchAirbnbHeroSlides: useCallback(() => {
      return handleApiCall(
        () => apiService.getAirbnbHeroSlides(),
        (data) => ({
          type: actionTypes.SET_AIRBNB_CONTENT,
          payload: { key: 'heroSlides', value: data, loadingKey: 'airbnbHeroSlides' },
        }),
        'airbnbHeroSlides'
      );
    }, [handleApiCall]),

    // Airbnb About Section
    fetchAirbnbAboutSection: useCallback(() => {
      return handleApiCall(
        () => apiService.getAirbnbAboutSection(),
        (data) => ({
          type: actionTypes.SET_AIRBNB_CONTENT,
          payload: { key: 'aboutSection', value: data, loadingKey: 'airbnbAboutSection' },
        }),
        'airbnbAboutSection'
      );
    }, [handleApiCall]),

    // Airbnb Property Section
    fetchAirbnbPropertySection: useCallback(() => {
      return handleApiCall(
        () => apiService.getAirbnbPropertySection(),
        (data) => ({
          type: actionTypes.SET_AIRBNB_CONTENT,
          payload: { key: 'propertySection', value: data, loadingKey: 'airbnbPropertySection' },
        }),
        'airbnbPropertySection'
      );
    }, [handleApiCall]),

    // Airbnb Explore Section
    fetchAirbnbExploreSection: useCallback(() => {
      return handleApiCall(
        () => apiService.getAirbnbExploreSection(),
        (data) => ({
          type: actionTypes.SET_AIRBNB_CONTENT,
          payload: { key: 'exploreSection', value: data, loadingKey: 'airbnbExploreSection' },
        }),
        'airbnbExploreSection'
      );
    }, [handleApiCall]),

    // Airbnb Team Section
    fetchAirbnbTeamSection: useCallback(() => {
      return handleApiCall(
        () => apiService.getAirbnbTeamSection(),
        (data) => ({
          type: actionTypes.SET_AIRBNB_CONTENT,
          payload: { key: 'teamSection', value: data, loadingKey: 'airbnbTeamSection' },
        }),
        'airbnbTeamSection'
      );
    }, [handleApiCall]),

    // Airbnb Contact Section
    fetchAirbnbContactSection: useCallback(() => {
      return handleApiCall(
        () => apiService.getAirbnbContactSection(),
        (data) => ({
          type: actionTypes.SET_AIRBNB_CONTENT,
          payload: { key: 'contactSection', value: data, loadingKey: 'airbnbContactSection' },
        }),
        'airbnbContactSection'
      );
    }, [handleApiCall]),

    // Airbnb Testimonials
    fetchAirbnbTestimonials: useCallback(() => {
      return handleApiCall(
        () => apiService.getAirbnbTestimonials(),
        (data) => ({
          type: actionTypes.SET_AIRBNB_CONTENT,
          payload: { key: 'testimonials', value: data, loadingKey: 'airbnbTestimonials' },
        }),
        'airbnbTestimonials'
      );
    }, [handleApiCall]),

    fetchFeaturedAirbnbTestimonials: useCallback(() => {
      return handleApiCall(
        () => apiService.getFeaturedAirbnbTestimonials(),
        (data) => ({
          type: actionTypes.SET_FEATURED_CONTENT,
          payload: { key: 'airbnbTestimonials', value: data },
        }),
        'airbnbTestimonials'
      );
    }, [handleApiCall]),

    // Airbnb Blog Section
    fetchAirbnbBlogSection: useCallback(() => {
      return handleApiCall(
        () => apiService.getAirbnbBlogSection(),
        (data) => ({
          type: actionTypes.SET_AIRBNB_CONTENT,
          payload: { key: 'blogSection', value: data, loadingKey: 'airbnbBlogSection' },
        }),
        'airbnbBlogSection'
      );
    }, [handleApiCall]),

    // Airbnb Blog Posts
    fetchAirbnbBlogPosts: useCallback(() => {
      return handleApiCall(
        () => apiService.getAirbnbBlogPosts(),
        (data) => ({
          type: actionTypes.SET_AIRBNB_CONTENT,
          payload: { key: 'blogPosts', value: data, loadingKey: 'airbnbBlogPosts' },
        }),
        'airbnbBlogPosts'
      );
    }, [handleApiCall]),

    fetchFeaturedAirbnbBlogPosts: useCallback(() => {
      return handleApiCall(
        () => apiService.getFeaturedAirbnbBlogPosts(),
        (data) => ({
          type: actionTypes.SET_FEATURED_CONTENT,
          payload: { key: 'airbnbBlogPosts', value: data },
        }),
        'airbnbBlogPosts'
      );
    }, [handleApiCall]),

    // Accommodations
    fetchAccommodations: useCallback(() => {
      return handleApiCall(
        () => apiService.getAccommodations(),
        (data) => ({
          type: actionTypes.SET_AIRBNB_CONTENT,
          payload: { key: 'accommodations', value: data, loadingKey: 'accommodations' },
        }),
        'accommodations'
      );
    }, [handleApiCall]),

    fetchFeaturedAccommodations: useCallback(() => {
      return handleApiCall(
        () => apiService.getFeaturedAccommodations(),
        (data) => ({
          type: actionTypes.SET_FEATURED_CONTENT,
          payload: { key: 'accommodations', value: data },
        }),
        'accommodations'
      );
    }, [handleApiCall]),

    // Accommodation Types
    fetchAccommodationTypes: useCallback(() => {
      return handleApiCall(
        () => apiService.getAccommodationTypes(),
        (data) => ({
          type: actionTypes.SET_AIRBNB_CONTENT,
          payload: { key: 'accommodationTypes', value: data, loadingKey: 'accommodationTypes' },
        }),
        'accommodationTypes'
      );
    }, [handleApiCall]),

    // Amenities
    fetchAmenities: useCallback(() => {
      return handleApiCall(
        () => apiService.getAmenities(),
        (data) => ({
          type: actionTypes.SET_AIRBNB_CONTENT,
          payload: { key: 'amenities', value: data, loadingKey: 'amenities' },
        }),
        'amenities'
      );
    }, [handleApiCall]),

    // AirTicket Methods
    // AirTicket Hero Slides
    fetchAirTicketHeroSlides: useCallback(() => {
      return handleApiCall(
        () => apiService.getAirTicketHeroSlides(),
        (data) => ({
          type: actionTypes.SET_AIRTICKET_CONTENT,
          payload: { key: 'heroSlides', value: data, loadingKey: 'airTicketHeroSlides' },
        }),
        'airTicketHeroSlides'
      );
    }, [handleApiCall]),

    // AirTicket Destinations
    fetchAirTicketDestinations: useCallback((params) => {
      return handleApiCall(
        () => apiService.getAirTicketDestinations(params),
        (data) => ({
          type: actionTypes.SET_AIRTICKET_CONTENT,
          payload: { key: 'destinations', value: data, loadingKey: 'airTicketDestinations' },
        }),
        'airTicketDestinations'
      );
    }, [handleApiCall]),

    fetchFeaturedAirTicketDestinations: useCallback(() => {
      return handleApiCall(
        () => apiService.getFeaturedAirTicketDestinations(),
        (data) => ({
          type: actionTypes.SET_FEATURED_CONTENT,
          payload: { key: 'airTicketDestinations', value: data },
        }),
        'airTicketDestinations'
      );
    }, [handleApiCall]),

    // AirTicket Categories
    fetchAirTicketCategories: useCallback(() => {
      return handleApiCall(
        () => apiService.getAirTicketCategories(),
        (data) => ({
          type: actionTypes.SET_AIRTICKET_CONTENT,
          payload: { key: 'categories', value: data, loadingKey: 'airTicketCategories' },
        }),
        'airTicketCategories'
      );
    }, [handleApiCall]),

    // AirTicket About Section
    fetchAirTicketAboutSection: useCallback(() => {
      return handleApiCall(
        () => apiService.getAirTicketAboutSection(),
        (data) => ({
          type: actionTypes.SET_AIRTICKET_CONTENT,
          payload: { key: 'aboutSection', value: data, loadingKey: 'airTicketAboutSection' },
        }),
        'airTicketAboutSection'
      );
    }, [handleApiCall]),

    // AirTicket Tours
    fetchAirTicketTours: useCallback((params) => {
      return handleApiCall(
        () => apiService.getAirTicketTours(params),
        (data) => ({
          type: actionTypes.SET_AIRTICKET_CONTENT,
          payload: { key: 'tours', value: data, loadingKey: 'airTicketTours' },
        }),
        'airTicketTours'
      );
    }, [handleApiCall]),

    fetchFeaturedAirTicketTours: useCallback(() => {
      return handleApiCall(
        () => apiService.getFeaturedAirTicketTours(),
        (data) => ({
          type: actionTypes.SET_FEATURED_CONTENT,
          payload: { key: 'airTicketTours', value: data },
        }),
        'airTicketTours'
      );
    }, [handleApiCall]),

    // AirTicket Gallery
    fetchAirTicketGallery: useCallback((params) => {
      return handleApiCall(
        () => apiService.getAirTicketGallery(params),
        (data) => ({
          type: actionTypes.SET_AIRTICKET_CONTENT,
          payload: { key: 'gallery', value: data, loadingKey: 'airTicketGallery' },
        }),
        'airTicketGallery'
      );
    }, [handleApiCall]),

    fetchFeaturedAirTicketGallery: useCallback(() => {
      return handleApiCall(
        () => apiService.getFeaturedAirTicketGallery(),
        (data) => ({
          type: actionTypes.SET_FEATURED_CONTENT,
          payload: { key: 'airTicketGallery', value: data },
        }),
        'airTicketGallery'
      );
    }, [handleApiCall]),

    // AirTicket Agents
    fetchAirTicketAgents: useCallback((params) => {
      return handleApiCall(
        () => apiService.getAirTicketAgents(params),
        (data) => ({
          type: actionTypes.SET_AIRTICKET_CONTENT,
          payload: { key: 'agents', value: data, loadingKey: 'airTicketAgents' },
        }),
        'airTicketAgents'
      );
    }, [handleApiCall]),

    fetchFeaturedAirTicketAgents: useCallback(() => {
      return handleApiCall(
        () => apiService.getFeaturedAirTicketAgents(),
        (data) => ({
          type: actionTypes.SET_FEATURED_CONTENT,
          payload: { key: 'airTicketAgents', value: data },
        }),
        'airTicketAgents'
      );
    }, [handleApiCall]),

    // AirTicket Services
    fetchAirTicketServices: useCallback((params) => {
      return handleApiCall(
        () => apiService.getAirTicketServices(params),
        (data) => ({
          type: actionTypes.SET_AIRTICKET_CONTENT,
          payload: { key: 'services', value: data, loadingKey: 'airTicketServices' },
        }),
        'airTicketServices'
      );
    }, [handleApiCall]),

    // AirTicket Contact
    fetchAirTicketContact: useCallback(() => {
      return handleApiCall(
        () => apiService.getAirTicketContact(),
        (data) => ({
          type: actionTypes.SET_AIRTICKET_CONTENT,
          payload: { key: 'contact', value: data, loadingKey: 'airTicketContact' },
        }),
        'airTicketContact'
      );
    }, [handleApiCall]),

    // AirTicket Testimonials
    fetchAirTicketTestimonials: useCallback((params) => {
      return handleApiCall(
        () => apiService.getAirTicketTestimonials(params),
        (data) => ({
          type: actionTypes.SET_AIRTICKET_CONTENT,
          payload: { key: 'testimonials', value: data, loadingKey: 'airTicketTestimonials' },
        }),
        'airTicketTestimonials'
      );
    }, [handleApiCall]),

    fetchFeaturedAirTicketTestimonials: useCallback(() => {
      return handleApiCall(
        () => apiService.getFeaturedAirTicketTestimonials(),
        (data) => ({
          type: actionTypes.SET_FEATURED_CONTENT,
          payload: { key: 'airTicketTestimonials', value: data },
        }),
        'airTicketTestimonials'
      );
    }, [handleApiCall]),

    // AirTicket Brands
    fetchAirTicketBrands: useCallback((params) => {
      return handleApiCall(
        () => apiService.getAirTicketBrands(params),
        (data) => ({
          type: actionTypes.SET_AIRTICKET_CONTENT,
          payload: { key: 'brands', value: data, loadingKey: 'airTicketBrands' },
        }),
        'airTicketBrands'
      );
    }, [handleApiCall]),

    // AirTicket Blog Posts
    fetchAirTicketBlogPosts: useCallback((params) => {
      return handleApiCall(
        () => apiService.getAirTicketBlogPosts(params),
        (data) => ({
          type: actionTypes.SET_AIRTICKET_CONTENT,
          payload: { key: 'blogPosts', value: data, loadingKey: 'airTicketBlogPosts' },
        }),
        'airTicketBlogPosts'
      );
    }, [handleApiCall]),

    fetchFeaturedAirTicketBlogPosts: useCallback(() => {
      return handleApiCall(
        () => apiService.getFeaturedAirTicketBlogPosts(),
        (data) => ({
          type: actionTypes.SET_FEATURED_CONTENT,
          payload: { key: 'airTicketBlogPosts', value: data },
        }),
        'airTicketBlogPosts'
      );
    }, [handleApiCall]),
  };

  return (
    <TourismContext.Provider value={state}>
      <TourismDispatchContext.Provider value={actions}>
        {children}
      </TourismDispatchContext.Provider>
    </TourismContext.Provider>
  );
};

// Custom hooks
export const useTourism = () => {
  const context = useContext(TourismContext);
  if (!context) {
    throw new Error('useTourism must be used within a TourismProvider');
  }
  return context;
};

export const useTourismActions = () => {
  const context = useContext(TourismDispatchContext);
  if (!context) {
    throw new Error('useTourismActions must be used within a TourismProvider');
  }
  return context;
};

// Combined hook for convenience
export const useTourismData = () => {
  const state = useTourism();
  const actions = useTourismActions();
  return { ...state, ...actions };
};

export default TourismContext;
